import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Heart, Shield, TrendingUp, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_COLOR = {
  new: "bg-stone text-forest",
  in_review: "bg-warning/15 text-warning",
  approved: "bg-success/15 text-success",
  rejected: "bg-danger/15 text-danger",
};

const STATUS_LABEL = {
  new: "New",
  in_review: "In Review",
  approved: "Approved",
  rejected: "Rejected",
};

export default function AgentDashboard() {
  const { agent } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stats").then((r) => {
      setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const cards = stats
    ? [
        { label: "Total clients", value: stats.total, icon: Users, hint: `${stats.last_7_days} new in last 7 days`, testId: "stat-total" },
        { label: "Health applications", value: stats.by_type.health, icon: Heart, hint: "Health insurance pipeline", testId: "stat-health" },
        { label: "Life applications", value: stats.by_type.life, icon: Shield, hint: "Life insurance pipeline", testId: "stat-life" },
        { label: "Last 7 days", value: stats.last_7_days, icon: TrendingUp, hint: "New submissions", testId: "stat-recent" },
      ]
    : [];

  return (
    <div className="p-6 lg:p-10">
      <header className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-2">Overview</div>
        <h1 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">
          Welcome back, {agent?.name?.split(" ")[0]}.
        </h1>
        <p className="text-sage mt-2">Here's what's happening across your client portfolio.</p>
      </header>

      {loading ? (
        <div className="text-sage" data-testid="dashboard-loading">Loading…</div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {cards.map((c) => (
              <div
                key={c.label}
                data-testid={c.testId}
                className="bg-white border border-line rounded-sm p-6 hover:-translate-y-[1px] hover:shadow-sm transition-transform duration-300"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="text-xs uppercase tracking-[0.15em] text-sage font-medium">{c.label}</div>
                  <c.icon className="w-4 h-4 text-clay" strokeWidth={1.7} />
                </div>
                <div className="font-heading text-4xl text-forest font-medium mb-1">{c.value}</div>
                <div className="text-xs text-sage">{c.hint}</div>
              </div>
            ))}
          </div>

          {/* Status breakdown + recent */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <section className="lg:col-span-1 bg-white border border-line rounded-sm p-6">
              <div className="text-xs uppercase tracking-[0.15em] text-sage font-medium mb-5">By status</div>
              <ul className="space-y-3">
                {Object.entries(stats?.by_status || {}).map(([key, val]) => (
                  <li
                    key={key}
                    className="flex items-center justify-between"
                    data-testid={`status-row-${key}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full ${
                        key === "new" ? "bg-sage" :
                        key === "in_review" ? "bg-warning" :
                        key === "approved" ? "bg-success" : "bg-danger"
                      }`} />
                      <span className="text-sm text-forest">{STATUS_LABEL[key]}</span>
                    </div>
                    <span className="font-heading text-xl text-forest">{val}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="lg:col-span-2 bg-white border border-line rounded-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs uppercase tracking-[0.15em] text-sage font-medium">Recent submissions</div>
                <Link
                  to="/agent/clients"
                  className="text-sm text-clay hover:text-claydark inline-flex items-center gap-1 transition-colors duration-200"
                  data-testid="view-all-clients-link"
                >
                  View all <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              {stats?.recent_submissions?.length ? (
                <ul className="divide-y divide-line">
                  {stats.recent_submissions.map((c) => (
                    <li key={c.id} className="py-3 flex items-center justify-between">
                      <Link
                        to={`/agent/clients/${c.id}`}
                        className="text-forest hover:text-clay font-medium transition-colors duration-200"
                        data-testid={`recent-client-${c.id}`}
                      >
                        {c.full_name}
                      </Link>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-sage capitalize">{c.insurance_type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_COLOR[c.status]}`}>
                          {STATUS_LABEL[c.status]}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-sage py-8 text-center">
                  No submissions yet. Share your application links with prospects.
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
